import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MetricsService, DetailedMetrics, MetricEntry } from '../../services/metrics.service';
import { Observable } from 'rxjs';

interface MetricsDisplay {
  title: string;
  data: MetricEntry[];
  total: number;
}

@Component({
  selector: 'app-metrics',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './metrics.component.html',
  styleUrls: ['./metrics.component.scss']
})
export class MetricsComponent implements OnInit {
  detailedMetrics$!: Observable<DetailedMetrics>;
  isLoading = true;
  selectedTimeFrame = 'overall';
  selectedOperation = 'all';

  // Display data organized for the UI
  displayMetrics: {
    overall: MetricsDisplay[];
    monthly: { [key: string]: MetricsDisplay[] };
    yearly: { [key: string]: MetricsDisplay[] };
  } = {
    overall: [],
    monthly: {},
    yearly: {}
  };

  constructor(private metricsService: MetricsService) {}

  ngOnInit(): void {
    console.log('📊 Metrics component initialized');
    this.loadMetrics();
  }

  loadMetrics(): void {
    this.isLoading = true;
    
    this.detailedMetrics$ = this.metricsService.getDetailedMetrics();
    
    this.detailedMetrics$.subscribe({
      next: (metrics) => {
        console.log('📊 Detailed metrics received:', metrics);
        this.processMetricsForDisplay(metrics);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ Error loading metrics:', error);
        this.isLoading = false;
      }
    });
  }

  private processMetricsForDisplay(metrics: DetailedMetrics): void {
    // Process overall metrics
    this.displayMetrics.overall = [
      {
        title: 'Create Operations',
        data: metrics.overall.create,
        total: metrics.overall.create.reduce((sum, entry) => sum + entry.value, 0)
      },
      {
        title: 'Update Operations', 
        data: metrics.overall.update,
        total: metrics.overall.update.reduce((sum, entry) => sum + entry.value, 0)
      },
      {
        title: 'Delete Operations',
        data: metrics.overall.delete,
        total: metrics.overall.delete.reduce((sum, entry) => sum + entry.value, 0)
      }
    ];

    // Process monthly metrics
    Object.keys(metrics.monthly).forEach(monthKey => {
      this.displayMetrics.monthly[monthKey] = [
        {
          title: 'Create Operations',
          data: metrics.monthly[monthKey].create,
          total: metrics.monthly[monthKey].create.reduce((sum, entry) => sum + entry.value, 0)
        },
        {
          title: 'Update Operations',
          data: metrics.monthly[monthKey].update,
          total: metrics.monthly[monthKey].update.reduce((sum, entry) => sum + entry.value, 0)
        },
        {
          title: 'Delete Operations',
          data: metrics.monthly[monthKey].delete,
          total: metrics.monthly[monthKey].delete.reduce((sum, entry) => sum + entry.value, 0)
        }
      ];
    });

    // Process yearly metrics
    Object.keys(metrics.yearly).forEach(year => {
      this.displayMetrics.yearly[year] = [
        {
          title: 'Create Operations',
          data: metrics.yearly[year].create,
          total: metrics.yearly[year].create.reduce((sum, entry) => sum + entry.value, 0)
        },
        {
          title: 'Update Operations',
          data: metrics.yearly[year].update,
          total: metrics.yearly[year].update.reduce((sum, entry) => sum + entry.value, 0)
        },
        {
          title: 'Delete Operations',
          data: metrics.yearly[year].delete,
          total: metrics.yearly[year].delete.reduce((sum, entry) => sum + entry.value, 0)
        }
      ];
    });
  }

  getMonthlyKeys(): string[] {
    return Object.keys(this.displayMetrics.monthly).sort((a, b) => {
      // Sort by year-month in descending order (most recent first)
      return b.localeCompare(a);
    });
  }

  getYearlyKeys(): string[] {
    return Object.keys(this.displayMetrics.yearly).sort((a, b) => {
      // Sort by year in descending order (most recent first)
      return parseInt(b) - parseInt(a);
    });
  }

  formatMonthKey(monthKey: string): string {
    const [year, month] = monthKey.split('-');
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  }

  getEventTypeLabel(eventType: string): string {
    switch (eventType) {
      case 'eventby4': return 'User Events (Path 4)';
      case 'eventby6': return 'Card Events (Path 6)';
      case 'eventby8': return 'Bookmark Events (Path 8)';
      default: return eventType;
    }
  }

  getOperationIcon(operation: string): string {
    switch (operation) {
      case 'create': return '➕';
      case 'update': return '✏️';
      case 'delete': return '🗑️';
      default: return '📊';
    }
  }

  refreshMetrics(): void {
    this.loadMetrics();
  }
}
